import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs, Spinner } from '@amzn/awsui-components-react';
import { useHistory, useLocation, useRouteMatch, Link } from 'react-router-dom';

import DetailsTab from './DetailsTab/DetailsTab';
import CustomersTab from './CustomersTab/CustomersTab';
import InvoicesTab from './InvoicesTab/InvoicesTab';
import NotesTab from './NotesTab/NotesTab';
import HistoryTab from './HistoryTab/HistoryTab';
import AuditTab from '../../../../common/components/AuditTrail/AuditTab';

import {
    getSelectedActivity,
    selectError,
    selectSelectedActivity,
    selectIsLoading,
    setSelectedActivity,
} from '../../../store/slices/selectedActivitySlice';
import { formatString } from '../../../../common/utils/formatString';
import { Actions } from '../../../../common/constants/auth';
import { check, getUserRoles } from '../../../../common/utils/auth';
import {
    ActivityStatus,
    ActivityType,
    AudienceType,
    ActivityLMSType,
    isACI,
    requiresCustomerCommitmentsValidation,
    validateClassSizeConsistency,
} from '../Common/Common';
import { ActivityData } from '../../../interfaces/activity';
import {
    Alert,
    Box,
    Button,
    FormField,
    Header,
    Input,
    Modal,
    SpaceBetween,
    TextContent,
    Link as CloudscapeLink,
} from '@amzn/awsui-components-react-v3';
import Can from '../../../../common/components/Can';
import {
    getActivities,
    getActivityById,
    mergeActivityIntoActivity,
} from '../../../services/activity-service';
import MergeActivityList from '../../Common/MergeActivity/MergeActivityList';
import { useNotifications } from '../../../../common/context/grimsbyNotifications';
import './ActivityDetails.scss';
import { selectFeatures } from '../../../../common/store/slices/featureSlice';
import { checkFeature } from '../../../../common/utils/featureFlag';
import { useHelpPanel } from '../../../../common/context/HelpPanelContext';
import { ACTIVITY_HELP_PANEL_CONTENT } from '../../../../common/components/HelpPanel/HelpPanelContent';
import { InfoLink } from '../../../../common/components/InfoLink/InfoLink';
import { ACTIVITY_PATH } from '../../../constants/path';
import {
    fetchActivityGroupDetailsOnly,
    selectActivityGroupError,
    selectActivityGroupLoading,
} from '../../../store/slices/activityGroupSlice';

const ActivityDetails = () => {
    const match = useRouteMatch<{ id: string }>();
    const error = useSelector(selectError);
    const isLoading = useSelector(selectIsLoading);
    const activity = useSelector(selectSelectedActivity);
    const featureFlags = useSelector(selectFeatures);
    const dispatch = useDispatch();
    const roles = getUserRoles();
    const location = useLocation();
    const history = useHistory();
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [mergeActivities, setMergeActivities] = useState<Array<any>>([]);
    const [mergeActivity, setMergeActivity] = useState<ActivityData | null>(
        null,
    );
    const [showConfirmMerge, setShowConfirmMerge] = useState(false);
    const [isMerging, setIsMerging] = useState(false);
    const [confirmMergeValue, setConfirmMergeValue] = useState<string>('');
    const [confirmMergeError, setConfirmMergeError] = useState<string | null>(
        null,
    );
    const [isLoadingMergeList, setIsLoadingMergeList] = useState<boolean>(true);
    const [showMustSelectActivityForMerge, setShowMustSelectActivityForMerge] =
        useState(false);

    const [activeTabId, setActiveTabId] = useState('details');
    const [showParentGroupGroupError, setShowParentGroupGroupError] =
        useState(false);

    const {
        actions: { makeHelpPanelHandler },
    } = useHelpPanel();

    const CONFIRM_MERGE_STRING = 'CONFIRM MERGE';
    const isACIFeatureEnabled = checkFeature(
        '',
        { featureName: 'aci_support' },
        featureFlags?.features,
    );
    const canShowWaitlistEnabledComponent = checkFeature(
        '',
        { featureName: 'waitlist-option-select' },
        featureFlags?.features,
    );

    const { addNotification } = useNotifications();

    const shouldShowInvoicesTab = (activityData: ActivityData) => {
        // we need to show invoices tab only for
        // public or private onsite classes and for Commercial programs only
        const hasActivityTypeEquality =
            activityData.activity_type === ActivityType.Class;
        const isPublicAccessible =
            activityData.activity_audience === AudienceType.Public;
        const isCommercialPrivateAccessible =
            activityData.activity_audience === AudienceType.CommercialPrivate ||
            activityData.activity_audience ===
                AudienceType.CommercialSemiPrivate;
        const isPublicOrComercialPrivateAccessible =
            isPublicAccessible || isCommercialPrivateAccessible;
        const isCommercialProgram = activityData.program === 'Commercial';
        const isDedicatedCloudProgram =
            activityData.program === 'Dedicated Cloud';

        if (
            (hasActivityTypeEquality &&
                isPublicOrComercialPrivateAccessible &&
                isCommercialProgram) ||
            (hasActivityTypeEquality &&
                isCommercialPrivateAccessible &&
                isDedicatedCloudProgram)
        ) {
            return true;
        }
        return false;
    };

    const activityGroupIsLoading = useSelector(selectActivityGroupLoading);
    const activityGroupDataError = useSelector(selectActivityGroupError);

    useEffect(() => {
        if (activityGroupDataError) {
            setShowParentGroupGroupError(true);
        }
    }, [activityGroupDataError]);

    useEffect(() => {
        dispatch(getSelectedActivity(match.params.id));
    }, [dispatch, match.params.id]);

    useEffect(() => {
        if (location.search) {
            const urlParams = new URLSearchParams(location.search);
            handleSetActiveTab(urlParams.get('tabId'));
        }
    }, [activeTabId, history]);

    const handleActivityDetailsUpdated = async () => {
        const activityData = await getActivityById(match.params.id);

        if (activityData?.activity) {
            dispatch(setSelectedActivity(activityData));
        }
    };

    const getMergeActivities = async () => {
        if (activity?.activity) {
            setShowMustSelectActivityForMerge(false);
            setIsLoadingMergeList(true);
            const activityData = activity.activity;
            const startDeliverySession =
                activityData.delivery_sessions[0].start_timestamp;
            const endDeliverySession =
                activityData.delivery_sessions[
                    activityData.delivery_sessions.length - 1
                ].end_timestamp;

            const activityResponse = await getActivities({
                delivery_country: [activityData.delivery_country],
                delivery_city: [activityData.delivery_city],
                course_name: [activityData.course_name],
                activity_modality: [activityData.activity_modality],
                delivery_language: [activityData.delivery_language],
                start_timestamp: startDeliverySession,
                end_timestamp: endDeliverySession,
            });

            setMergeActivities(
                activityResponse.activities.filter((a: any) => {
                    return a.pk !== activity.activity.pk;
                }),
            );

            setIsLoadingMergeList(false);
        }
    };

    const mergeActivityFormProps: any = {
        mergeActivities,
        setMergeActivity,
        autoMergeOn: true,
        mergeDescription: (
            <span>
                Merging this activity adds the customer(s) from this activity to
                an existing activity with the same course, language, location,
                modality and start date.
            </span>
        ),
        activityPk: activity?.activity.pk,
        showMustSelect: showMustSelectActivityForMerge,
        isLoadingMergeList,
    };

    const confirmMergeActivity = async () => {
        if (confirmMergeValue !== CONFIRM_MERGE_STRING) {
            setConfirmMergeError(`This field is required to merge.`);
            return;
        } else {
            setConfirmMergeError(null);
        }

        if (activity?.activity && mergeActivity) {
            setIsMerging(true);
            const isSuccessful = await mergeActivityIntoActivity(
                mergeActivity.pk,
                activity.activity.pk,
            );
            setIsMerging(false);

            setShowConfirmMerge(false);
            setShowMergeModal(false);

            addNotification({
                id: `merge-activity`,
                ...(isSuccessful
                    ? {
                          type: 'success',
                          content: <span>Activity merged.</span>,
                      }
                    : {
                          type: 'error',
                          content:
                              'An error occurred while merging the activity.',
                      }),
            });

            if (isSuccessful) {
                history.push({
                    pathname: `/activities/${mergeActivity.pk}`,
                });
            }
        }
    };

    const submitMerge = () => {
        if (!mergeActivity) {
            setShowMustSelectActivityForMerge(true);
        } else {
            setShowConfirmMerge(true);
        }
    };

    const handleSetActiveTab = (param) => {
        const params = new URLSearchParams({ tabId: param });
        history.push({
            search: `?${params.toString()}`,
        });
        setActiveTabId(param);
    };

    if (error) {
        const isNotFoundError = error.message === 'Activity does not exist.';

        return (
            <Alert
                data-testid="ActivityDetailsError"
                type="error"
                header={isNotFoundError ? '404 Not Found' : 'Error'}
            >
                <SpaceBetween size="xxxs">
                    <TextContent>
                        <p>
                            {isNotFoundError
                                ? 'The requested activity cannot be found in our system. '
                                : error.message ||
                                  'An unknown error occurred. '}
                            {isNotFoundError && (
                                <CloudscapeLink
                                    href={ACTIVITY_PATH.BASE}
                                    variant="primary"
                                >
                                    Return to Activity List
                                </CloudscapeLink>
                            )}
                        </p>
                    </TextContent>
                </SpaceBetween>
            </Alert>
        );
    } else if (!isLoading && activity?.activity) {
        const activityData = activity.activity;
        const shouldShowCustomerTab = true; // Always show customers tab
        const isAciActivity = isACI(
            activityData.activity_group_name,
            isACIFeatureEnabled,
        );

        return (
            <div data-testid="ActivityDetailsPage">
                {showParentGroupGroupError && (
                    <Alert
                        data-testid="ActivityGroupDataError"
                        onDismiss={() => setShowParentGroupGroupError(false)}
                        dismissible
                        type="error"
                        header={activityGroupDataError.title}
                    >
                        {activityGroupDataError.message}
                    </Alert>
                )}
                {activity.activity.activity_status ===
                    ActivityStatus.Canceled && (
                    <Alert
                        onDismiss={() => false}
                        visible={true}
                        header="This activity has been cancelled"
                        data-testid="CancelledActivityAlert"
                    >
                        Information is displayed here for historical reference
                        and cannot be edited.
                    </Alert>
                )}
                {activity.activity.lms_type === ActivityLMSType.Kiku && (
                    <Alert
                        type="warning"
                        header="Kiku LMS deprecation"
                        data-testid="kiku-deprecation-banner"
                    >
                        Kiku will no longer support class delivery after March
                        31, 2026.
                    </Alert>
                )}
                {requiresCustomerCommitmentsValidation(activityData) &&
                    !validateClassSizeConsistency(activityData).isValid && (
                        <Alert
                            type="warning"
                            header="Class size data needs review"
                            data-testid="class-size-mismatch-banner"
                        >
                            The stored class size (
                            {validateClassSizeConsistency(activityData)
                                .classSize ?? 0}
                            ) does not match the sum of customer commitments (
                            {validateClassSizeConsistency(activityData).sum}).
                            Please review and save the customer "Students
                            committed" values to update the class size.
                        </Alert>
                    )}
                <Header
                    info={
                        <InfoLink
                            onFollow={makeHelpPanelHandler(
                                ACTIVITY_HELP_PANEL_CONTENT.title,
                            )}
                        />
                    }
                    variant="h1"
                    actions={
                        <Can
                            perform={Actions.ACTIVITY_MODIFY}
                            yes={() => (
                                <SpaceBetween direction="horizontal" size="xs">
                                    {isAciActivity && (
                                        <Button
                                            loading={activityGroupIsLoading}
                                            disabled={
                                                activityData.activity_status ===
                                                ActivityStatus.Canceled
                                            }
                                            disabledReason="Cannot edit a canceled activity group."
                                            data-testid="EditGroupButton"
                                            onClick={() => {
                                                dispatch(
                                                    fetchActivityGroupDetailsOnly(
                                                        {
                                                            name: activityData.activity_group_name,
                                                            history,
                                                            navigationPath: `/activities/group/`,
                                                        },
                                                    ),
                                                );
                                            }}
                                        >
                                            View group
                                        </Button>
                                    )}
                                    <Button
                                        data-testid="MergeActivityButton"
                                        onClick={() => {
                                            setShowMergeModal(true);
                                            getMergeActivities();
                                        }}
                                    >
                                        Merge activity
                                    </Button>
                                    <Link
                                        to={`/activities/${activityData.pk}/clone`}
                                    >
                                        <Button data-testid="CloneActivityButton">
                                            Clone activity
                                        </Button>
                                    </Link>
                                </SpaceBetween>
                            )}
                        />
                    }
                >
                    <span data-testid="ActivityTitle">
                        {formatString(activityData.activity_name)}
                    </span>
                </Header>
                <Modal
                    size={'max'}
                    visible={showMergeModal}
                    onDismiss={() => {
                        setShowMergeModal(false);
                    }}
                    header={'Merge activity'}
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button
                                    variant="link"
                                    onClick={() => {
                                        setShowMergeModal(false);
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        submitMerge();
                                    }}
                                    disabled={mergeActivities.length === 0}
                                >
                                    Merge activity
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    <Modal
                        size={'medium'}
                        visible={showConfirmMerge}
                        onDismiss={() => {
                            setShowConfirmMerge(false);
                        }}
                        header={'Merge activity?'}
                        footer={
                            <Box float="right">
                                <SpaceBetween direction="horizontal" size="xs">
                                    <Button
                                        variant="link"
                                        onClick={() => {
                                            setShowConfirmMerge(false);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        disabled={isMerging}
                                        onClick={() => {
                                            confirmMergeActivity();
                                        }}
                                    >
                                        {isMerging
                                            ? 'Merging activity...'
                                            : 'Merge'}
                                    </Button>
                                </SpaceBetween>
                            </Box>
                        }
                    >
                        <p>Merge this activity with the selected activity?</p>
                        <div className="grimsby-sub-section-divider" />
                        <FormField
                            label={`To continue, enter "CONFIRM MERGE" and press the merge button.`}
                            errorText={confirmMergeError}
                        >
                            <Input
                                value={confirmMergeValue}
                                placeholder="Enter confirmation message"
                                onChange={(e) => {
                                    setConfirmMergeValue(e.detail.value);
                                }}
                            />
                        </FormField>
                    </Modal>
                    <MergeActivityList {...mergeActivityFormProps} />
                </Modal>
                <Tabs
                    data-testid="ActivityDetailsPageTabSection"
                    tabs={[
                        {
                            label: 'Details',
                            id: 'details',
                            content: (
                                <DetailsTab
                                    activity={activityData}
                                    onActivityDetailsUpdated={
                                        handleActivityDetailsUpdated
                                    }
                                    isACIFeatureEnabled={isACIFeatureEnabled}
                                />
                            ),
                        },
                        ...(check(roles, Actions.ACTIVITY_CUSTOMERS_VIEW) &&
                        shouldShowCustomerTab
                            ? [
                                  {
                                      label: 'Customers',
                                      id: 'customers',
                                      content: (
                                          <CustomersTab
                                              activity={activityData}
                                          />
                                      ),
                                  },
                              ]
                            : []),
                        ...(check(roles, Actions.ACTIVITY_INVOICES_VIEW) &&
                        shouldShowInvoicesTab(activityData)
                            ? [
                                  {
                                      label: 'Invoices',
                                      id: 'invoices',
                                      content: (
                                          <InvoicesTab
                                              activity={activityData}
                                          />
                                      ),
                                  },
                              ]
                            : []),
                        ...(check(roles, Actions.ACTIVITY_NOTES_VIEW)
                            ? [
                                  {
                                      label: 'Notes',
                                      id: 'notes',
                                      content: (
                                          <NotesTab
                                              id={match.params.id}
                                              activity={activityData}
                                          />
                                      ),
                                  },
                              ]
                            : []),
                        ...(check(roles, Actions.ACTIVITY_HISTORY_VIEW)
                            ? [
                                  {
                                      label: 'History',
                                      id: 'history',
                                      content: (
                                          <HistoryTab activity={activityData} />
                                      ),
                                  },
                              ]
                            : []),
                        ...(check(roles, Actions.ACTIVITY_AUDIT_VIEW)
                            ? [
                                  {
                                      label: 'Audit Log',
                                      id: 'auditLog',
                                      content: (
                                          <AuditTab id={match.params.id} />
                                      ),
                                  },
                              ]
                            : []),
                    ]}
                    activeTabId={activeTabId}
                    onChange={(e) => {
                        handleSetActiveTab(e.detail.activeTabId);
                    }}
                />
            </div>
        );
    } else {
        return <Spinner data-testid="ActivityDetailsSpinner" />;
    }
};

export default ActivityDetails;
